import { useQuery } from '@tanstack/react-query';
import type { FatiaDoRelatorio, GranularidadeDeRelatorio, PontoDaSerie } from '@central/contracts';
import { formatarData } from '@/lib/format';
import {
  Carregando,
  Contagem,
  Falhou,
  Numero,
  Painel,
  ResumoDoRecorte,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import {
  GraficoDeBarras,
  GraficoDeRosca,
  GraficoDeSerie,
} from '@/features/relatorios/graficos';
import {
  chavesDeRelatorio,
  obterMix,
  obterVolume,
} from '@/features/relatorios/relatorios.api';

/**
 * Volume: quanto entra, quanto sai, e de onde vem.
 *
 * A razão incidente/requisição fica nesta aba, e não numa própria, porque é a
 * mesma pergunta vista por outro ângulo: uma operação que abre três incidentes
 * para cada requisição não tem problema de volume, tem problema de estabilidade.
 */

/** Colunas de uma quebra por dimensão — as mesmas em todos os painéis. */
function colunasDeFatia(total: number): ReadonlyArray<ColunaDeTabela<FatiaDoRelatorio>> {
  return [
    { cabecalho: 'Item', celula: (linha) => linha.rotulo, csv: (linha) => linha.rotulo },
    {
      cabecalho: 'Chamados',
      alinharADireita: true,
      celula: (linha) => linha.total.toLocaleString('pt-BR'),
      csv: (linha) => linha.total,
    },
    {
      cabecalho: 'Participação',
      alinharADireita: true,
      celula: (linha) => porcento(linha.total, total),
      csv: (linha) => porcento(linha.total, total),
    },
  ];
}

const porcento = (parte: number, todo: number) =>
  todo === 0 ? '—' : `${((parte / todo) * 100).toFixed(1).replace('.', ',')}%`;

const somaDe = (fatias: readonly FatiaDoRelatorio[]) =>
  fatias.reduce((soma, fatia) => soma + fatia.total, 0);

export function VolumePage() {
  const { consulta } = useFiltro();

  const volume = useQuery({
    queryKey: chavesDeRelatorio.de('volume', consulta),
    queryFn: () => obterVolume(consulta),
  });
  const mix = useQuery({
    queryKey: chavesDeRelatorio.de('mix', consulta),
    queryFn: () => obterMix(consulta),
  });

  if (volume.isPending) return <Carregando />;
  if (volume.isError) return <Falhou erro={volume.error} aoTentar={() => void volume.refetch()} />;

  const dados = volume.data;
  const saldo = dados.abertos - dados.encerrados;

  return (
    <div className="animate-slide-up space-y-6">
      <ResumoDoRecorte recorte={dados.recorte} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Contagem rotulo="Abertos no período" valor={dados.abertos} />
        <Contagem rotulo="Encerrados no período" valor={dados.encerrados} />
        <Contagem
          rotulo="Saldo da fila"
          valor={saldo}
          tom={saldo > 0 ? 'atencao' : 'bom'}
          ajuda={saldo > 0 ? 'A fila cresceu no período.' : 'A fila drenou no período.'}
        />
        {mix.data ? (
          <Numero
            rotulo="Incidentes por requisição"
            indicador={mix.data.razao}
            formatar={(valor) => valor.toFixed(2).replace('.', ',')}
            ajuda={`${mix.data.incidentes} incidente(s) · ${mix.data.requisicoes} requisição(ões)`}
          />
        ) : (
          <Contagem rotulo="Incidentes por requisição" valor={0} />
        )}
      </div>

      <Painel
        titulo="Entradas e saídas ao longo do período"
        descricao="Duas curvas de propósito: a distância entre elas é o que faz a fila crescer ou drenar."
        indicador={dados.serie}
        grafico={(pontos) => (
          <GraficoDeSerie
            dados={pontos.map((ponto) => ({
              rotulo: rotuloDeBalde(ponto.inicio, dados.recorte.granularidade),
              abertos: ponto.abertos,
              encerrados: ponto.encerrados,
            }))}
          />
        )}
        colunas={() => colunasDaSerie(dados.recorte.granularidade)}
        chaveDe={(ponto) => ponto.inicio}
        nome={['relatorio', 'volume', 'serie']}
        recorte={dados.recorte}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <Painel
          titulo="Por tipo"
          indicador={dados.porTipo}
          grafico={(linhas) => <GraficoDeRosca dados={linhas.map(paraBarra)} />}
          colunas={(linhas) => colunasDeFatia(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'volume', 'por-tipo']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por situação"
          descricao="A situação de agora dos chamados abertos no período — não a que eles tinham quando abriram."
          indicador={dados.porSituacao}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} />}
          colunas={(linhas) => colunasDeFatia(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'volume', 'por-situacao']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por equipe"
          indicador={dados.porEquipe}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} />}
          colunas={(linhas) => colunasDeFatia(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'volume', 'por-equipe']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por organização"
          indicador={dados.porOrganizacao}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} />}
          colunas={(linhas) => colunasDeFatia(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'volume', 'por-organizacao']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por prioridade"
          indicador={dados.porPrioridade}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} />}
          colunas={(linhas) => colunasDeFatia(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'volume', 'por-prioridade']}
          recorte={dados.recorte}
        />
      </div>
    </div>
  );
}

const paraBarra = (fatia: FatiaDoRelatorio) => ({ rotulo: fatia.rotulo, total: fatia.total });

function colunasDaSerie(
  granularidade: GranularidadeDeRelatorio,
): ReadonlyArray<ColunaDeTabela<PontoDaSerie>> {
  return [
    {
      cabecalho: granularidade === 'mes' ? 'Mês' : granularidade === 'semana' ? 'Semana de' : 'Dia',
      celula: (ponto) => rotuloDeBalde(ponto.inicio, granularidade),
      csv: (ponto) => rotuloDeBalde(ponto.inicio, granularidade),
    },
    {
      cabecalho: 'Abertos',
      alinharADireita: true,
      celula: (ponto) => ponto.abertos,
      csv: (ponto) => ponto.abertos,
    },
    {
      cabecalho: 'Encerrados',
      alinharADireita: true,
      celula: (ponto) => ponto.encerrados,
      csv: (ponto) => ponto.encerrados,
    },
    {
      cabecalho: 'Saldo',
      alinharADireita: true,
      celula: (ponto) => ponto.abertos - ponto.encerrados,
      csv: (ponto) => ponto.abertos - ponto.encerrados,
    },
  ];
}

const MES = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' });

function rotuloDeBalde(iso: string, granularidade: GranularidadeDeRelatorio): string {
  if (granularidade === 'mes') return MES.format(new Date(iso));
  return formatarData(iso);
}
